import React, { useEffect } from "react";
import styled, { keyframes } from "styled-components";
import { useParams, useNavigate, Navigate } from "react-router-dom";
import BackgroundDesign from "../components/BackgroundDesign";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Link } from "react-router-dom";
import {
  faCalendarAlt,
  faMapMarkerAlt,
  faClock,
  faArrowLeft,
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";

// Animations
const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(30px); }
  to { opacity: 1; transform: translateY(0); }
`;

// Styled Components
const PageWrapper = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  position: relative;
`;

const CampaignHeader = styled.div.attrs(props => ({
  style: {
    backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url(${props.image})`,
  }
}))`
  position: relative;
  height: 100vh;
  width: 100%;
  padding-top: 120px;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;

  &::after {
    content: "";
    position: absolute;
    bottom: -1px;
    left: 0;
    right: 0;
    height: 150px;
    background: linear-gradient(
      to top,
      ${(props) => props.theme.colors.background},
      transparent
    );
    z-index: 2;
  }

  @media (max-width: 768px) {
    padding-top: 80px;
    height: 75vh;
  }
`;

const HeaderContent = styled.div`
  position: absolute;
  bottom: 150px;
  left: 0;
  right: 0;
  z-index: 3;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  color: white;
  animation: ${fadeIn} 1s ease-out;

  @media (max-width: 768px) {
    bottom: 100px;
    padding: 0 1rem;
  }
`;

const BackButton = styled.button`
  background: transparent;
  color: white;
  border: 2px solid white;
  padding: 0.5rem 1rem;
  border-radius: 50px;
  margin-bottom: 1.5rem;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    transform: translateX(-5px);
  }
`;

const CampaignTitle = styled.h1`
  font-size: clamp(2rem, 5vw, 3.5rem);
  font-weight: 800;
  padding: 1rem;
  margin-bottom: 1rem;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const CampaignMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 2rem;
  margin-top: 1.5rem;

  @media (max-width: 768px) {
    gap: 1rem;
  }
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;

  svg {
    color: ${(props) => props.theme.colors.secondary};
  }
`;

const MainContent = styled.main`
  position: relative;
  z-index: 10;
  margin-top: -4rem;
  padding-bottom: 4rem;
`;

const ContentContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 2rem;
  padding: 0 2rem;

  @media (max-width: 992px) {
    grid-template-columns: 1fr;
  }
`;

const CampaignDetails = styled.div`
  background: white;
  border-radius: 20px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  animation: ${slideUp} 0.8s ease-out;
`;

const TabsContainer = styled.div`
  display: flex;
  border-bottom: 1px solid #eee;
`;

const Tab = styled.button`
  flex: 1;
  padding: 1.5rem 2rem;
  background: ${(props) => (props.active ? "white" : "#f9f9f9")};
  border: none;
  border-bottom: 3px solid
    ${(props) => (props.active ? props.theme.colors.primary : "transparent")};
  font-weight: ${(props) => (props.active ? "700" : "500")};
  color: ${(props) => (props.active ? props.theme.colors.text : "#777")};
  cursor: pointer;
  transition: all 0.3s ease;
  min-width: 120px;
  white-space: nowrap;

  &:hover {
    color: ${(props) => props.theme.colors.primary};
  }

  @media (max-width: 768px) {
    padding: 1rem;
    font-size: 0.9rem;
    min-width: 100px;
  }
`;

const TabContent = styled.div`
  padding: 2.5rem;

  @media (max-width: 768px) {
    padding: 1.5rem;
  }
`;

const Paragraph = styled.p`
  margin-bottom: 1.5rem;
  line-height: 1.8;
  color: #444;
  font-size: 1.05rem;

  strong {
    color: ${(props) => props.theme.colors.primary};
    font-weight: 700;
  }
`;

const SectionTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  margin: 2.5rem 0 1.5rem;
  position: relative;
  padding-left: 1rem;
  color: ${(props) => props.theme.colors.text};

  &::before {
    content: "";
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 4px;
    background: ${(props) => props.theme.colors.primary};
    border-radius: 10px;
  }
`;

const HighlightBox = styled.div`
  background: #f9f9f9;
  border-left: 4px solid ${(props) => props.theme.colors.secondary};
  padding: 1.5rem;
  margin: 2rem 0;
  border-radius: 0 10px 10px 0;
`;

const ImageGallery = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  background: ${(props) => props.theme.colors.background};
  margin-bottom: 2rem;
`;

const MainGalleryContainer = styled.div`
  position: relative;
  border-radius: 15px;
  overflow: hidden;
  aspect-ratio: 4/3;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
`;

const GalleryImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
  background-color: #000;
  transition: all 0.5s ease;
  display: block;
`;

const GalleryButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  ${(props) => (props.direction === "left" ? "left: 1rem;" : "right: 1rem;")}
  background: ${props => props.theme.colors.primary};
  color: white;
  border: none;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.3s ease;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: ${props => props.theme.colors.secondary};
    transform: translateY(-50%) scale(1.1);
  }
  
  svg {
    font-size: 1.2rem;
  }
  
  @media (max-width: 768px) {
    width: 35px;
    height: 35px;
    ${(props) => (props.direction === "left" ? "left: 0.5rem;" : "right: 0.5rem;")}
  }
`;

const ThumbnailContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
  gap: 0.5rem;
  margin-top: 1rem;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fill, minmax(70px, 1fr));
  }
`;

const ThumbnailImage = styled.img`
  width: 100%;
  aspect-ratio: 1;
  object-fit: cover;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  border: 3px solid ${props => props.isActive ? props.theme.colors.primary : 'transparent'};
  filter: ${props => props.isActive ? 'brightness(100%)' : 'brightness(70%)'};
  
  &:hover {
    transform: translateY(-3px);
    filter: brightness(100%);
  }
`;

const GalleryHeading = styled(SectionTitle)`
  display: flex;
  align-items: center;
  
  span {
    margin-left: auto;
    font-size: 1rem;
    color: ${props => props.theme.colors.textLight};
    font-weight: 400;
  }
`;

const ImageOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(to bottom, rgba(0,0,0,0) 60%, rgba(0,0,0,0.7) 100%);
  z-index: 1;
  display: flex;
  align-items: flex-end;
  padding: 1.5rem;
  opacity: 0;
  transition: opacity 0.3s ease;
  
  .caption {
    color: white;
    font-size: 1.1rem;
    font-weight: 500;
  }
  
  &:hover {
    opacity: 1;
  }
`;

const UpdateItem = styled.div`
  background: #f9f9f9;
  border-radius: 10px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
`;

const UpdateDate = styled.div`
  font-size: 0.9rem;
  color: ${(props) => props.theme.colors.secondary};
  font-weight: 600;
  margin-bottom: 0.5rem;
`;

const UpdateTitle = styled.h4`
  font-size: 1.2rem;
  font-weight: 700;
  margin-bottom: 1rem;
  color: ${(props) => props.theme.colors.text};
`;

const SidebarContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;

  @media (max-width: 992px) {
    grid-row: 1;
  }
`;

const SidebarCard = styled.div`
  background: white;
  border-radius: 20px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  padding: 2rem;
  animation: ${slideUp} 0.8s ease-out;
  animation-delay: 0.2s;
  opacity: 0;
  animation-fill-mode: forwards;
`;

const ProgressContainer = styled.div`
  margin-bottom: 2rem;
`;

const ProgressHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-bottom: 1rem;

  h3 {
    font-size: 2rem;
    font-weight: 800;
    color: ${(props) => props.theme.colors.text};

    span {
      font-size: 1rem;
      opacity: 0.6;
      font-weight: 500;
    }
  }

  p {
    font-weight: 600;
    color: ${(props) => props.theme.colors.secondary};
  }
`;

const ProgressBar = styled.div`
  height: 10px;
  background: #eee;
  border-radius: 10px;
  overflow: hidden;
  margin-bottom: 1rem;
`;

const ProgressFill = styled.div`
  background: #b5873a;
  height: 100%;
  width: ${(props) => props.$progress}%;
  border-radius: 10px;
  transition: width 1.5s ease;
`;

const CampaignStats = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  margin-bottom: 2rem;
`;

const StatItem = styled.div`
  text-align: center;

  h4 {
    font-size: 1.5rem;
    font-weight: 700;
    color: ${(props) => props.theme.colors.primary};
    margin-bottom: 0.5rem;
  }

  p {
    font-size: 0.9rem;
    color: #666;
  }
`;

const DonateButton = styled.button`
  width: 100%;
  background: ${(props) => props.theme.colors.primary};
  color: white;
  border: none;
  padding: 1.25rem;
  border-radius: 10px;
  font-weight: 700;
  font-size: 1.1rem;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 10px 25px ${(props) => props.theme.colors.primary}33;
  }
`;

const SharingOptions = styled.div`
  display: flex;
  justify-content: center;
  gap: 1rem;
  margin-top: 1.5rem;
`;

const SocialButton = styled.button`
  background: #f2f2f2;
  border: none;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-3px);
    background: ${(props) => props.theme.colors.secondary};
    color: white;
  }
`;

// Helper function to calculate days left until the event
const calculateDaysLeft = (dateString) => {
  if (!dateString) return 0;
  const eventDate = new Date(dateString);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  eventDate.setHours(0, 0, 0, 0);

  const diffTime = eventDate - today;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  return diffDays > 0 ? diffDays : 0;
};

// Helper function to calculate progress percentage
const calculateProgress = (raised, goal) => {
  if (!goal || goal === 0) return 0;
  const progress = (raised / goal) * 100;
  return Math.min(Math.round(progress * 10) / 10, 100); // Round to 1 decimal, cap at 100%
};

const campaignsData = {
  1: {
    id: 1,
    title: "Heartbeat Journey: Spreading Hope, One Step at a Time",
    location: "Across 12 regions, Morocco",
    date: "November 2024 - 2031",
    image: "/images/sliders/SLIDE_HBJ_main.webp",
    raised: 15000,
    goal: 25000,
    donors: 128,
    daysLeft: 0,
    progress: 60,
    showStats: false,
    content: {
      overview: `From 2024 to 2031, across the 12 regions of Morocco, Heartbeat Journey is set on a mission to transform lives. With a goal of renovating 24 schools, this project brings hope, care, and change to the most remote villages. It is not just about offering aid, it is about building a brighter, more dignified future for those who need it most.`,
      details: [
        {
          title: "Restoring Schools, Empowering the Future",
          content: `Education is the key to change, but many schools in rural areas lack basic facilities, proper classrooms, and essential learning tools. As part of Heartbeat Journey, we are committed to renovating 24 schools, creating a safe and inspiring learning environment for children.

Beyond restoring walls and classrooms, we are bringing technology into these schools by providing:
- Computers to open doors to digital learning
- Projectors (Datashow) for a more interactive education
- Printers to support teachers and students
- A stock of essential school supplies to ensure every child has the tools to succeed

Every classroom we restore, every piece of technology we install, and every book we place on a desk is a step toward a stronger, more educated generation.`,
        },
        {
          title: "Supporting Families in Need",
          content: `In many villages, families struggle to meet their basic needs. Winters can be harsh, and resources are often scarce. That's why Heartbeat Journey steps in to provide food baskets, warm clothing, and blankets, ensuring that no one is left behind in the cold. More than just material aid, we bring comfort, dignity, and the reassurance that they are not forgotten.`,
        },
        {
          title: "Bringing Healthcare to Remote Areas",
          content: `Access to medical care should not be a privilege, it is a right. Yet, in many remote villages, hospitals and clinics are far away, and basic healthcare is out of reach. Through our medical caravans, we bring doctors, nurses, and medical professionals directly to the people. Whether it's a simple check-up, necessary medication, or urgent treatment, we work to ensure that every person has the chance to live a healthier life.`,
        },
      ],
      conclusion: `A Journey of Big Hearts, for Bigger Change. Heartbeat Journey is not just about providing aid, it's about creating long-lasting change. It's about empowering communities, bringing smiles to children's faces, and proving that kindness can transform lives. With every mission, we carry the belief that small actions, when done with love, can make the biggest impact.

Together, with big hearts and strong hands, we are shaping a better world, one heartbeat at a time.`,
    },
    updates: [
      {
        date: "Feb 15, 2024",
        title: "Project Kickoff",
        content:
          "We're thrilled to announce the official kickoff of the Heartbeat Journey project! After months of planning and coordination, we're ready to begin this transformative 7-year journey across Morocco.",
      },
      {
        date: "Feb 28, 2024",
        title: "First Region Planning Complete",
        content:
          "Our team has completed detailed planning for the first region. We've identified 2 schools in critical need of renovation and established partnerships with local organizations to support our efforts in these communities.",
      },
      {
        date: "Mar 10, 2024",
        title: "Member Recruitment",
        content:
          "We're currently recruiting Members for our first major school renovation project. If you have skills in construction, education, healthcare, or simply want to help, please reach out to our Member coordinator at asso.thebigheart@tbh.ma.",
      },
    ],
    gallery: [
      // "/images/heartbeat-gallery-1.jpg",
      // "/images/heartbeat-gallery-2.jpg",
      // "/images/heartbeat-gallery-3.jpg"
    ],
  },
  2: {
    id: 2,
    title: "1st Action: Pilot Project in Douar Al Byadna",
    location: "Marrakech-Safi Region, Morocco",
    date: "Nov 15-17, 2023",
    image: "/images/HeartbeatJourney-Actions/hb-v1/SLIDE_HBJv1_sans.webp",
    raised: 25100,
    goal: 30000,
    donors: 75,
    daysLeft: 0,
    progress: 84,
    showStats: true,
    content: {
      overview: `From November 15 to 17, the Heartbeat Journey project took its first step in Douar Al Byadna, Errhamna, Marrakech-Safi, marking the beginning of a journey filled with hope, change, and new possibilities. This pilot action was much more than just a project, it was a renewal of dreams and opportunities for the children of this village.`,
      details: [
        {
          title: "Classroom Renovations",
          content: `We brought classrooms back to life, creating a space where students can learn, grow, and aspire to a brighter future. The renovations included repairing walls, installing new windows, enhancing lighting, and creating a more comfortable learning environment.`,
        },
        {
          title: "Technology Integration",
          content: `Computers were introduced, opening doors to knowledge, technology, and endless possibilities. By bringing digital tools into the classroom, we're helping bridge the digital divide and preparing students for the modern world.`,
        },
        {
          title: "Educational Supplies",
          content: `Essential school supplies were offered to every child, ensuring that they have everything they need to write their own success stories. From notebooks and pens to textbooks and art supplies, each student received the tools needed for effective learning.`,
        },
        {
          title: "Interactive Learning Activities",
          content: `Beyond the physical improvements, we enriched the learning experience by organizing training and interactive activities, making education more dynamic, engaging, and joyful. It was a time of sharing, laughter, and inspiration, reminding us that true change begins with education and empowerment.`,
        },
      ],
      conclusion: `This first step is just the beginning of a transformative journey, where every action, no matter how small, contributes to building a better future for Morocco's young generations. With every school we renovate and every child we support, we are shaping a world where education, hope, and opportunity reach every corner. The Heartbeat Journey has begun, and the best is yet to come.`,
    },
    updates: [
      {
        date: "Nov 17, 2023",
        title: "Pilot Project Successfully Completed",
        content:
          "We're proud to announce the successful completion of our pilot project in Douar Al Byadna! Over three intensive days, our team renovated the local school, installed computer equipment, and distributed educational supplies to all students.",
      },
      {
        date: "Nov 30, 2023",
        title: "Impact Assessment Results",
        content:
          "Our follow-up assessment shows significant improvements in student attendance and engagement following the renovations. Teachers report that the new technology is already enhancing the learning experience for students of all ages.",
      },
      {
        date: "Dec 15, 2023",
        title: "Planning Next Steps",
        content:
          "Based on the success of our pilot project, we're now finalizing plans for our comprehensive 7-year Heartbeat Journey initiative. Stay tuned for exciting announcements about our expansion to all 12 regions of Morocco.",
      },
      {
        date: "Jan 20, 2024",
        title: "Partnership Announcement",
        content:
          "We're thrilled to announce new partnerships with three major educational foundations that will help us scale our impact. These collaborations will provide additional resources, expertise, and funding to support our mission.",
      },
    ],
    gallery: [
      "/images/HeartbeatJourney-Actions/hb-v1/get.jpeg",
      "/images/HeartbeatJourney-Actions/hb-v1/get (1).jpeg",
      "/images/HeartbeatJourney-Actions/hb-v1/get (2).jpeg",
      "/images/HeartbeatJourney-Actions/hb-v1/get (3).jpeg",
      "/images/HeartbeatJourney-Actions/hb-v1/get (4).jpeg",
      "/images/HeartbeatJourney-Actions/hb-v1/get (5).jpeg",
      "/images/HeartbeatJourney-Actions/hb-v1/get (6).jpeg",
    ],
  },
  3: {
    id: 3,
    title: "2nd Action Béni Mellal-Khénifra Region",
    location: "Béni Mellal, Morocco",
    date: "Apr 10, 2024",
    image: "/images/HeartbeatJourney-Actions/hb-v2/SLIDE_HBJv2_sans_hbjv2.webp",
    raised: 18000,
    goal: 30000,
    donors: 60,
    daysLeft: 0,
    progress: 60,
    showStats: true,
    content: {
      overview: `The Second Action continues our commitment to transforming educational facilities in rural Morocco. Building on the success of our pilot project, we're expanding our efforts to create lasting change in the Douar Al Byadna community.`,
      details: [
        {
          title: "A Light at the Mountain Summit",
          content: `From April 25 to 27, 2025, The Big Heart Association carried out the second phase of the Heartbeat Journey project in the village of Sferssid, nestled in the heart of the mountains in the Béni Mellal-Khénifra region. A remote and hard-to-reach area, yet rich in human strength and urgent need for support.

Despite winding roads and challenging conditions, our team, driven by the conviction that every child deserves a dignified education, undertook a deep and humane mission. The local school was completely renovated and equipped with everything necessary: computers, bookshelves, lamps, school supplies… every detail carefully planned to create an environment conducive to learning and growth.

In addition, workshops, computer training sessions, and various activities were organized to support and raise awareness among beneficiaries about the new resources available to them.

But the effort didn’t stop there. Food baskets were distributed to families in precarious situations, clothing was provided, and for the first time in the association’s history, a successful medical caravan was organized. More than 400 people benefited from free consultations in a dedicated space, with medications distributed according to individual needs.

This action marked a turning point: it was concrete proof that even in the most remote places, with determination, solidarity, and love, it is possible to bring about real and lasting change.`,
        }
      ],
      conclusion: `This second phase represents our ongoing commitment to sustainable educational development in Morocco's rural communities.`,
    },
    updates: [],
    gallery: [
      "/images/HeartbeatJourney-Actions/hb-v2/hb-v2-pic1.webp",
      "/images/HeartbeatJourney-Actions/hb-v2/hb-v2-pic2.webp",
      "/images/HeartbeatJourney-Actions/hb-v2/hb-v2-pic3.webp",
      "/images/HeartbeatJourney-Actions/hb-v2/hb-v2-pic4.webp",
      "/images/HeartbeatJourney-Actions/hb-v2/hb-v2-pic5.webp",
      "/images/HeartbeatJourney-Actions/hb-v2/hb-v2-pic6.webp",
      "/images/HeartbeatJourney-Actions/hb-v2/hb-v2-pic7.webp",
      "/images/HeartbeatJourney-Actions/hb-v2/hb-v2-pic8.webp",
      "/images/HeartbeatJourney-Actions/hb-v2/hb-v2-pic9.webp",
      "/images/HeartbeatJourney-Actions/hb-v2/hb-v2-pic10.webp",
    ],
  },
  4: {
    id: 4,
    title: "3rd Action Douar Dar El Khanous",
    location: "Commune Zitouna, Tangier-Tetouan-Al Hoceima Region, Morocco",
    date: "March 27–29, 2026",
    eventDate: "2026-03-27", // Used for automatic countdown calculation
    image: `${process.env.PUBLIC_URL}/images/sliders/MAIN-SLIDE-HBJv3.webp`,
    raised: 33020,
    goal: 150000,
    donors: 58,
    progress: 0,
    showStats: true,
    content: {
      overview: `From March 27 to 29, 2026, The Big Heart Association carried out the third edition of the Heartbeat Journey project at Douar Dar El Khanous, in the Tanger-Tétouan-Al Hoceïma region — benefiting the students of Groupe Scolaire Dar El Ghazi and the families of the area. Through this action, we deployed a comprehensive intervention spanning education, healthcare, and social support.`,
      details: [
        {
          title: "School Renovation & Infrastructure",
          content: `We rehabilitated and improved the classrooms to create a more dignified and functional learning environment for students. A dedicated space for teachers was also arranged, providing them with the conditions they deserve to carry out their mission.`,
        },
        {
          title: "Digital Classroom Powered by INTELCIA",
          content: `A fully equipped computer room with 10 computers was set up, made possible through the valuable contribution of our partner INTELCIA. This digital infrastructure opens the door to new opportunities for students and marks a milestone in their educational journey.`,
        },
        {
          title: "Educational Equipment",
          content: `Teaching materials including a data projector (datashow), printer, and paper were installed to support both teachers and students in their daily activities and make learning more interactive and effective.`,
        },
        {
          title: "School Supplies Provided by BIC",
          content: `School supplies were distributed to students, made available through the generous contribution of BIC. Every child deserves the tools to learn — and this distribution ensured none were left without.`,
        },
        {
          title: "Medical Caravan Supported by LAPROPHAN",
          content: `A medical caravan was organized, providing nearly 200 consultations for community members. Medications were distributed according to individual needs, with part of the supply provided by LAPROPHAN. Access to healthcare should never depend on geography — and this caravan brought it directly to those who needed it most.`,
        },
        {
          title: "Humanitarian Aid",
          content: `Clothing was distributed to residents, and food baskets were delivered to families in need — offering both material support and a reminder that they are not forgotten.`,
        },
        {
          title: "Educational Workshops & Awareness",
          content: `Educational and awareness workshops were held for approximately 90 students, empowering them with knowledge and encouraging them to make the most of the new resources available to them.`,
        },
        {
          title: "Joy & Play Sponsored by FOODS AND GOODS SA",
          content: `Fun and recreational activities were organized for the children, accompanied by sweets offered by FOODS AND GOODS SA. Because childhood also means joy — and every child deserves a moment of happiness.`,
        },
      ],
      conclusion: `Through this third edition of Heartbeat Journey, we reinforced our commitment to taking concrete action in favor of education, healthcare access, and support for rural communities. We continue this journey with a clear conviction: every action counts, and every commitment creates a lasting impact. ❤️`,
    },
    updates: [],
    gallery: [
      `${process.env.PUBLIC_URL}/images/HeartbeatJourney-Actions/hb-v3/HBv4-5.webp`,
      `${process.env.PUBLIC_URL}/images/HeartbeatJourney-Actions/hb-v3/HBv4-2.webp`,
      `${process.env.PUBLIC_URL}/images/HeartbeatJourney-Actions/hb-v3/HBv4-3.webp`,
      `${process.env.PUBLIC_URL}/images/HeartbeatJourney-Actions/hb-v3/HBv4-4.webp`,
      `${process.env.PUBLIC_URL}/images/HeartbeatJourney-Actions/hb-v3/HBv4-1.webp`,
      `${process.env.PUBLIC_URL}/images/HeartbeatJourney-Actions/hb-v3/HBv4-6.webp`,
      `${process.env.PUBLIC_URL}/images/HeartbeatJourney-Actions/hb-v3/HBv4-7.webp`,
      `${process.env.PUBLIC_URL}/images/HeartbeatJourney-Actions/hb-v3/HBv4-8.webp`
    ],
  },
  5: {
    id: 5,
    title: "4th Action Douar Toufaress",
    location: "Commune Belfaa, Chtouka-Aït Baha, Souss-Massa Region, Morocco",
    date: "November 6–8, 2026",
    eventDate: "2026-11-06", // Used for automatic countdown calculation
    image: `${process.env.PUBLIC_URL}/images/HeartbeatJourney-Actions/hb-v4/SLIDE_HBJv4.png`,
    raised: 300,
    goal: 120000, // TODO: confirm the fundraising goal for the 4th edition
    donors: 1,
    progress: 0,
    showStats: true,
    content: {
      overview: `On November 6, 7 and 8, 2026, The Big Heart Association will carry out the fourth edition of the Heartbeat Journey project at Douar Toufaress, in the commune of Belfaa, Chtouka-Aït Baha province, Souss-Massa region — benefiting the students of Groupe Scolaire Imjad and the families of the area. Three editions behind us brought us together around one idea: turning solidarity into something you can see, touch, and measure in people's lives. This fourth stop continues that work through five actions spanning education, digital inclusion, solidarity, and healthcare.`,
      details: [
        {
          title: "School Renovation & Infrastructure",
          content: `We will rehabilitate and improve the school's spaces so that students learn in an environment that supports them instead of holding them back — a more dignified, more functional setting built around the people who use it every day.`,
        },
        {
          title: "Digital Classroom & Learning Equipment",
          content: `A computer room will be equipped with the machines and gear it needs, opening the door to skills and opportunities that remoteness has kept out of reach. School supplies will be provided alongside it, so no child is left without the basic tools to learn.`,
        },
        {
          title: "Workshops & Training",
          content: `Office-software training sessions and creative workshops will be organized for students, making sure the new equipment becomes a resource they actually know how to use rather than a room they walk past.`,
        },
        {
          title: "Clothing Distribution",
          content: `New and good-condition clothing will be collected, then distributed to the families and residents of the douar who need it — dignity delivered directly, without detour.`,
        },
        {
          title: "Food Baskets",
          content: `Food baskets covering a set of essential staples will be distributed to families in need, offering both material support and a reminder that they have not been forgotten.`,
        },
        {
          title: "Medical Caravan",
          content: `A medical caravan of doctors and nurses will provide free consultations and check-ups for the community, with medication distributed free of charge according to each person's needs. Access to healthcare should never depend on geography.`,
        },
      ],
      conclusion: `We believe development never starts with a single step — it starts with a set of steps that keep people at the centre of every initiative. This fourth edition is one more chance to bring volunteers, partners, and local stakeholders together around the same vision: act concretely, pass it on, and leave a lasting impact. ❤️`,
    },
    updates: [],
    gallery: [
      // The key visual doubles as the first gallery slide (avoids a duplicate 2 MB copy)
      `${process.env.PUBLIC_URL}/images/HeartbeatJourney-Actions/hb-v4/SLIDE_HBJv4.png`,
      `${process.env.PUBLIC_URL}/images/HeartbeatJourney-Actions/hb-v4/POSTER-HBJv4-school.jpg`,
      `${process.env.PUBLIC_URL}/images/HeartbeatJourney-Actions/hb-v4/POSTER-HBJv4-digital.png`,
      `${process.env.PUBLIC_URL}/images/HeartbeatJourney-Actions/hb-v4/POSTER-HBJv4-clothing.png`,
      `${process.env.PUBLIC_URL}/images/HeartbeatJourney-Actions/hb-v4/POSTER-HBJv4-food.png`,
      `${process.env.PUBLIC_URL}/images/HeartbeatJourney-Actions/hb-v4/POSTER-HBJv4-medical.png`,
    ],
  }
};

const HbeatJourneyDetailPage = () => {
  const { hbId } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = React.useState("about");
  const [currentImageIndex, setCurrentImageIndex] = React.useState(0);

  const campaign = campaignsData[parseInt(hbId)];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  if (!campaign) {
    return <Navigate to="/heartbeat-journey" />;
  }

  // Determine if we should show stats based on the campaign's showStats property
  const showStats = campaign?.showStats !== false;

  return (
    <PageWrapper>
      <BackgroundDesign />

      <CampaignHeader image={campaign.image}>
        <HeaderContent>
          <BackButton onClick={() => navigate("/heartbeat-journey")}>
            <FontAwesomeIcon icon={faArrowLeft} />
            Back to projects
          </BackButton>
          <CampaignTitle>{campaign.title}</CampaignTitle>
          <CampaignMeta>
            <MetaItem>
              <FontAwesomeIcon icon={faMapMarkerAlt} />
              <span>{campaign.location}</span>
            </MetaItem>
            <MetaItem>
              <FontAwesomeIcon icon={faCalendarAlt} />
              <span>{campaign.date}</span>
            </MetaItem>
            {showStats && calculateDaysLeft(campaign.eventDate) > 0 && (
              <MetaItem>
                <FontAwesomeIcon icon={faClock} />
                <span>{calculateDaysLeft(campaign.eventDate)} days left</span>
              </MetaItem>
            )}
          </CampaignMeta>
        </HeaderContent>
      </CampaignHeader>

      <MainContent>
        <ContentContainer>
          <CampaignDetails>
            <TabsContainer>
              <Tab
                active={activeTab === "about"}
                onClick={() => setActiveTab("about")}
              >
                About
              </Tab>
              {/* <Tab
                active={activeTab === "updates"}
                onClick={() => setActiveTab("updates")}
              >
                Updates
              </Tab> */}
              <Tab
                active={activeTab === "gallery"}
                onClick={() => setActiveTab("gallery")}
              >
                Gallery
              </Tab>
            </TabsContainer>

            <TabContent>
              {activeTab === "about" && (
                <>
                  <Paragraph>
                    <strong>{campaign.content.overview}</strong>
                  </Paragraph>

                  {campaign.content.details.map((section, index) => (
                    <div key={index}>
                      <SectionTitle>{section.title}</SectionTitle>
                      <Paragraph>{section.content}</Paragraph>
                    </div>
                  ))}

                  <HighlightBox>
                    <Paragraph>
                      <strong>{campaign.content.conclusion}</strong>
                    </Paragraph>
                  </HighlightBox>
                </>
              )}

              {activeTab === "updates" && (
                <>
                  <SectionTitle>Latest Updates</SectionTitle>
                  {campaign.updates && campaign.updates.length > 0 ? (
                    campaign.updates.map((update, index) => (
                      <UpdateItem key={index}>
                        <UpdateDate>{update.date}</UpdateDate>
                        <UpdateTitle>{update.title}</UpdateTitle>
                        <Paragraph>{update.content}</Paragraph>
                      </UpdateItem>
                    ))
                  ) : (
                    <Paragraph>
                      No updates available yet. Check back soon for news about
                      this project!
                    </Paragraph>
                  )}
                  <Paragraph>
                    Follow us on social media to get real-time updates about all
                    our activities and how you can contribute to make this
                    project a success.
                  </Paragraph>
                </>
              )}

              {activeTab === "gallery" && (
                <>
                  <GalleryHeading>
                    Project Gallery
                    {campaign.gallery && campaign.gallery.length > 0 && (
                      <span>{currentImageIndex + 1} / {campaign.gallery.length}</span>
                    )}
                  </GalleryHeading>

                  {!campaign.gallery || campaign.gallery.length === 0 ? (
                    <Paragraph>
                      No images available yet. Check back soon for photos from
                      this project!
                    </Paragraph>
                  ) : (
                    <ImageGallery>
                      <MainGalleryContainer>
                        <GalleryImage
                          src={campaign.gallery[currentImageIndex]}
                          alt={`Project gallery ${currentImageIndex + 1}`}
                        />
                        <ImageOverlay>
                          <div className="caption">Heartbeat Journey Project {currentImageIndex + 1}</div>
                        </ImageOverlay>
                        <GalleryButton
                          direction="left"
                          onClick={() =>
                            setCurrentImageIndex((prev) =>
                              prev === 0 ? campaign.gallery.length - 1 : prev - 1
                            )
                          }
                        >
                          <FontAwesomeIcon icon={faChevronLeft} />
                        </GalleryButton>
                        <GalleryButton
                          direction="right"
                          onClick={() =>
                            setCurrentImageIndex((prev) =>
                              prev === campaign.gallery.length - 1 ? 0 : prev + 1
                            )
                          }
                        >
                          <FontAwesomeIcon icon={faChevronRight} />
                        </GalleryButton>
                      </MainGalleryContainer>

                      <ThumbnailContainer>
                        {campaign.gallery.map((img, index) => (
                          <ThumbnailImage
                            key={index}
                            src={img}
                            alt={`Thumbnail ${index + 1}`}
                            isActive={index === currentImageIndex}
                            onClick={() => setCurrentImageIndex(index)}
                          />
                        ))}
                      </ThumbnailContainer>
                    </ImageGallery>
                  )}
                </>
              )}
            </TabContent>
          </CampaignDetails>

          <SidebarContainer>
            <SidebarCard>
              {showStats ? (
                <>
                  <ProgressContainer>
                    <ProgressHeader>
                      <h3>
                        MAD{campaign.raised.toLocaleString()}{" "}
                        <span>raised of MAD{campaign.goal.toLocaleString()}</span>
                      </h3>
                      <p>{calculateProgress(campaign.raised, campaign.goal)}%</p>
                    </ProgressHeader>
                    <ProgressBar>
                      <ProgressFill $progress={calculateProgress(campaign.raised, campaign.goal)} />
                    </ProgressBar>
                  </ProgressContainer>

                  <CampaignStats>
                    <StatItem>
                      <h4>{campaign.donors}</h4>
                      <p>Donors</p>
                    </StatItem>
                    <StatItem>
                      <h4>{calculateDaysLeft(campaign.eventDate)}</h4>
                      <p>Days Left</p>
                    </StatItem>
                    <StatItem>
                      <h4>1</h4>
                      <p>School</p>
                    </StatItem>
                  </CampaignStats>
                </>
              ) : (
                <>
                  <SectionTitle>Support This Initiative</SectionTitle>
                  <Paragraph>
                    Your contribution helps us continue this important work.
                    Every donation brings us one step closer to creating
                    meaningful change.
                  </Paragraph>
                </>
              )}
              <Link to="/donate">
                <DonateButton>Donate Now</DonateButton>
              </Link>

              <SharingOptions>
                <SocialButton>f</SocialButton>
                <SocialButton>t</SocialButton>
                <SocialButton>in</SocialButton>
              </SharingOptions>
            </SidebarCard>

            <SidebarCard>
              <SectionTitle>Project Team</SectionTitle>
              <Paragraph>
                This project is led by our dedicated members and staff who
                are passionate about creating positive change in Morocco.
              </Paragraph>
              <Paragraph>
                <strong>Contact:</strong> asso.thebigheart@tbh.ma
              </Paragraph>
            </SidebarCard>
          </SidebarContainer>
        </ContentContainer>
      </MainContent>
    </PageWrapper>
  );
};

export default HbeatJourneyDetailPage;
